import DOMPurify from 'dompurify'

/**
 * 消毒 HTML 内容，防止 XSS 攻击
 * @param {string} dirty - 可能包含恶意脚本的 HTML 字符串
 * @param {object} options - DOMPurify 配置选项
 * @returns {string} 安全的 HTML 字符串
 */
export function sanitizeHtml(dirty, options = {}) {
  if (!dirty) return ''

  const defaultOptions = {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'u', 'span', 'p', 'br', 'div', 'ul', 'ol', 'li', 'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'pre', 'code', 'blockquote'],
    ALLOWED_ATTR: ['class', 'style', 'href', 'target'],
    ALLOW_DATA_ATTR: false,
  }

  return DOMPurify.sanitize(dirty, { ...defaultOptions, ...options })
}

/**
 * 消毒并格式化文本（用于考试题目等，将 **word** 转为 <u>word</u>）
 * @param {string} text - 原始文本
 * @returns {string} 安全的格式化 HTML
 */
export function formatAndSanitize(text) {
  if (!text) return ''

  // 先进行消毒，然后再做格式化替换
  const sanitized = DOMPurify.sanitize(text, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: []
  })

  // 将 **word** 格式替换为 <u>word</u>
  return sanitized.replace(/\*\*(.+?)\*\*/g, '<u>$1</u>')
}

/**
 * 高亮搜索结果（安全版本）
 * @param {string} content - 内容
 * @param {string} query - 搜索词
 * @returns {string} 高亮后的安全 HTML
 */
export function highlightSearchResult(content, query) {
  if (!content || !query) return DOMPurify.sanitize(content || '')

  // 先消毒内容和查询词
  const sanitizedContent = DOMPurify.sanitize(content, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: []
  })

  // 转义搜索词中的正则特殊字符
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  const regex = new RegExp(`(${escapedQuery})`, 'gi')
  return sanitizedContent.replace(regex, '<span class="highlight">$1</span>')
}

/**
 * 消毒 AI 生成的内容（允许更多格式化标签）
 * @param {string} content - AI 生成的内容
 * @returns {string} 安全的内容
 */
export function sanitizeAIContent(content) {
  if (!content) return ''

  return DOMPurify.sanitize(content, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'u', 'span', 'p', 'br', 'div', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'pre', 'code', 'blockquote', 'table', 'tr', 'td', 'th', 'thead', 'tbody'],
    ALLOWED_ATTR: ['class', 'style'],
    ALLOW_DATA_ATTR: false,
  })
}

export default {
  sanitizeHtml,
  formatAndSanitize,
  highlightSearchResult,
  sanitizeAIContent
}
